import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { MyDispatch, MySelector } from "../../redux/store"
import { Box, Modal } from "@mui/material"
import { setAddToAlbumArray } from "../../redux/utilSlice"
import {
  btnsParent,
  leftBtn,
  returnStyle,
  rightBtn,
  title,
} from "../../utils/popupModalStyle"
import { HandleAsyncFns } from "../../hooks/handleAsyncFns"
import { PhysicalAlbum } from "../../types/physicalAlbum"
import { toast } from "sonner"
import Loader from "../utils/Loader"

const AddToAlbumModal = () => {
  const dispatch = useDispatch<MyDispatch>()

  const { addToAlbumArray } = MySelector((state) => state.util)
  const { physicalAlbums } = MySelector((state) => state.gallery)
  const { theme } = MySelector((state) => state.theme)

  const { addFilesToAlbum } = HandleAsyncFns()

  const [showModal, setShowModal] = useState(false)
  const [selectedAlbumId, setSelectedAlbumId] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    setShowModal(!!addToAlbumArray && addToAlbumArray.length > 0)
    if (!addToAlbumArray) setSelectedAlbumId(null)
  }, [addToAlbumArray])

  const close = () => {
    setShowModal(false)
    dispatch(setAddToAlbumArray(null))
  }

  const count = addToAlbumArray?.length || 0

  const submit = async () => {
    if (!selectedAlbumId) {
      toast.info("Please select an album")
      return
    }
    setSubmitting(true)
    try {
      await addFilesToAlbum(selectedAlbumId, addToAlbumArray || [])
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal open={showModal} onClose={close}>
      <Box sx={returnStyle(theme)}>
        <div className="p-5 md:px-[26.11px] md:py-[28px]">
          <p className={title}>
            Add {count} {count === 1 ? "photo" : "photos"} to album
          </p>

          <div className="mt-5 max-h-[45vh] overflow-y-auto space-y-2 text-left">
            {(!physicalAlbums || physicalAlbums.length === 0) && (
              <p className="text-sm text-offGrey text-center py-4">
                No albums available.
              </p>
            )}
            {physicalAlbums?.map((album: PhysicalAlbum) => {
              const selected = selectedAlbumId === album.id
              return (
                <div
                  key={album.id}
                  onClick={() => setSelectedAlbumId(album.id)}
                  className={`flex items-center justify-between gap-3 cursor-pointer px-3 py-2.5 rounded-xl border transition-all duration-150 ${
                    selected
                      ? "border-newDarkBlack dark:border-golden bg-offWhite dark:bg-blackish"
                      : "border-offGrey/40 hover:bg-offWhite dark:hover:bg-blackish"
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className={`h-4 w-4 rounded-full border shrink-0 flex items-center justify-center ${
                        selected
                          ? "border-newDarkBlack dark:border-golden"
                          : "border-offGrey"
                      }`}
                    >
                      {selected && (
                        <span className="h-2 w-2 rounded-full bg-newDarkBlack dark:bg-golden" />
                      )}
                    </span>
                    <p className="text-sm font-[500] text-black dark:text-brightWhite truncate">
                      {album.name}
                    </p>
                  </div>
                  <p className="text-xs text-offGrey shrink-0">
                    {album.fileCount || 0}
                    {album.fileLimit ? `/${album.fileLimit}` : ""}
                  </p>
                </div>
              )
            })}
          </div>

          <div className={btnsParent}>
            <button onClick={close} className={leftBtn}>
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={submitting || !selectedAlbumId}
              className={`${rightBtn} flex justify-center items-center`}
            >
              {submitting ? <Loader /> : "Add"}
            </button>
          </div>
        </div>
      </Box>
    </Modal>
  )
}

export default AddToAlbumModal
